import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import NavGroupPanel from '../primitives/NavGroupPanel';
import SidebarTriggerItem from './SidebarTriggerItem';
import SidebarLinkItem from './SidebarLinkItem';
import { NavNode } from '../types';

type SidebarGroupProps = {
	children: React.ReactNode;
	icon?: React.ReactNode;
	text: string;
	size?: 'default' | 'collapsed';
	navNodes?: NavNode[];
	childRoutes?: string[];
	onExpandSidebar?: () => void;
};

const getRoutesFromNodes = (nodes: NavNode[]): string[] => {
	return nodes.flatMap((node) => {
		if (node.type === 'link') return [node.to];
		return getRoutesFromNodes(node.children);
	});
};

const findActiveLink = (
	nodes: NavNode[],
	pathname: string,
): Extract<NavNode, { type: 'link' }> | undefined => {
	for (const node of nodes) {
		if (node.type === 'link' && pathname.startsWith(node.to)) return node;
		if (node.type === 'group') {
			const found = findActiveLink(node.children, pathname);
			if (found) return found;
		}
	}
};

const SidebarGroup = ({
	children,
	icon,
	text,
	size = 'default',
	navNodes = [],
	childRoutes = [],
	onExpandSidebar,
}: SidebarGroupProps) => {
	const location = useLocation();
	const hasActiveChild = childRoutes.some((route) => location.pathname.startsWith(route));
	const [open, setOpen] = useState(hasActiveChild);
	const isCollapsed = size === 'collapsed';

	const activeLink = useMemo(
		() => (!open && hasActiveChild ? findActiveLink(navNodes, location.pathname) : undefined),
		[open, hasActiveChild, navNodes, location.pathname],
	);

	const handleToggle = () => {
		if (isCollapsed && onExpandSidebar) {
			onExpandSidebar();
			setOpen(true);
			return;
		}
		setOpen((v) => !v);
	};

	useEffect(() => {
		if (hasActiveChild) setOpen(true);
	}, [hasActiveChild]);

	useEffect(() => {
		if (isCollapsed && !hasActiveChild) {
			setOpen(false);
		}
	}, [isCollapsed, hasActiveChild]);

	const showPanel = open && !isCollapsed;

	return (
		<li className='flex flex-col gap-2'>
			<SidebarTriggerItem
				text={text}
				expanded={open}
				onClick={handleToggle}
				icon={icon}
				size={size}
				selected={isCollapsed && hasActiveChild}
			/>
			{showPanel && <NavGroupPanel id={text}>{children}</NavGroupPanel>}
			{!open && !isCollapsed && activeLink && (
				<div className='pl-4'>
					<div className='border-l border-secondary pl-2'>
						<SidebarLinkItem
							text={activeLink.text}
							to={activeLink.to}
							tone={activeLink.tone}
							icon={activeLink.icon}
							size={size}
							groupId={text}
						/>
					</div>
				</div>
			)}
		</li>
	);
};

export { getRoutesFromNodes };

export default SidebarGroup;
